import { useRef, type ReactNode } from "react";

// A row of tabs over the page's panels, underlined where it is: /shares'
// Terminals, Agents and Recordings, a share's Timeline and Screen. A real
// tablist — the picked tab alone in the Tab order, ←/→ moving along them and
// picking as they go — whose panels the page renders, each a tabpanel
// labelled by its tab (`tabId`, `panelId`).

export type TabItem<T extends string> = { id: T; label: ReactNode };

export const tabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;
export const panelId = (prefix: string, id: string) => `${prefix}-panel-${id}`;

export function Tabs<T extends string>({ prefix, label, tabs, value, onChange }: {
  /** what the ids of the tabs and their panels start with, unique on the page */
  prefix: string;
  label: string;
  tabs: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
}) {
  const refs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const at = tabs.findIndex((x) => x.id === value);
    const next = tabs[(at + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length]!.id;
    onChange(next);
    refs.current[next]?.focus();
  };
  return (
    <div className="tab-bar" role="tablist" aria-label={label} onKeyDown={onKey}>
      {tabs.map(({ id, label }) => (
        <button
          key={id}
          ref={(el) => {
            refs.current[id] = el;
          }}
          type="button"
          role="tab"
          id={tabId(prefix, id)}
          aria-controls={panelId(prefix, id)}
          aria-selected={value === id}
          tabIndex={value === id ? 0 : -1}
          className="tab-bar-tab"
          onClick={() => onChange(id)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
